import { useMemo, useState } from 'react';
import type { MouseEvent } from 'react';
import CreatableSelect from 'react-select/creatable';
import type { DropdownIndicatorProps, IndicatorSeparatorProps, MultiValue, SingleValue } from 'react-select';
import { useTheme } from 'context/ThemeContext';
import FormInput from 'components/forms/FormInput';
import SolarIcon from 'components/icons/SolarIcon';
import { selectStylesDark, selectStylesLigth } from 'utils/helpers/structs/selectStyles';

export interface CreatableNameOption {
    value: number;
    label: string;
}

type CreatableNameSelectBaseProps = {
    options: CreatableNameOption[];
    onCreate: (name: string) => Promise<boolean>;
    placeholder?: string;
    addLabel: string;
    formatCreateLabel: (input: string) => string;
    requiredMessage: string;
    maxLength?: number;
    disabled?: boolean;
    /** false ховає кнопку додавання і створення нового значення зі списку. */
    allowCreate?: boolean;
    inputId?: string;
    className?: string;
    isClearable?: boolean;
};

export type CreatableNameSelectProps =
    | (CreatableNameSelectBaseProps & {
          isMulti?: false;
          value?: number | null;
          onChange: (option: CreatableNameOption | null) => void;
      })
    | (CreatableNameSelectBaseProps & {
          isMulti: true;
          value?: number[];
          onChange: (option: CreatableNameOption[]) => void;
      });

const stopSelectToggle = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
};

const NameDropdownChevron = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false" className="cursor-pointer">
        <path
            fill="currentColor"
            d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z"
        />
    </svg>
);

const CreatableNameSelect = ({
    options,
    value,
    isMulti = false,
    onChange,
    onCreate,
    placeholder,
    addLabel,
    formatCreateLabel,
    requiredMessage,
    maxLength,
    disabled = false,
    allowCreate = true,
    inputId,
    className = 'w-100',
    isClearable = true,
}: CreatableNameSelectProps) => {
    const { darkTheme } = useTheme();
    const [creating, setCreating] = useState(false);
    const [adding, setAdding] = useState(false);
    const selectTheme = darkTheme ? selectStylesDark : selectStylesLigth;

    const selectComponents = useMemo(() => {
        const dropdownIndicator = {
            DropdownIndicator: (indicatorProps: DropdownIndicatorProps<CreatableNameOption, boolean>) => (
                <div {...indicatorProps.innerProps} className="d-flex align-items-center px-2 text-secondary">
                    <NameDropdownChevron />
                </div>
            ),
        };
        if (!allowCreate) return dropdownIndicator;
        return {
            ...dropdownIndicator,
            IndicatorSeparator: (separatorProps: IndicatorSeparatorProps<CreatableNameOption, boolean>) => (
                <div className="d-flex align-items-center">
                    <button
                        type="button"
                        className="btn btn-link p-0 text-primary lh-1 fs-12"
                        disabled={separatorProps.isDisabled}
                        onMouseDown={stopSelectToggle}
                        onClick={(event) => {
                            stopSelectToggle(event);
                            setAdding(true);
                        }}
                    >
                        <SolarIcon name="plus" className="me-1" />
                        {addLabel}
                    </button>
                    <span className="border-start align-self-stretch my-2 ms-2" />
                </div>
            ),
        };
    }, [addLabel, allowCreate]);

    const handleCreate = async (inputValue: string): Promise<boolean> => {
        const name = String(inputValue || '').trim();
        if (!name || creating) return false;
        setCreating(true);
        try {
            const created = await onCreate(name);
            if (created) setAdding(false);
            return created;
        } catch {
            return false;
        } finally {
            setCreating(false);
        }
    };

    if (adding) {
        return (
            <FormInput
                compact
                autoFocus
                required
                requiredMessage={requiredMessage}
                maxLength={maxLength}
                showForm
                setShowForm={setAdding}
                submitLabel={addLabel}
                callBack={(input) => handleCreate(String(input ?? ''))}
            />
        );
    }

    const selectedValue = isMulti
        ? options.filter((opt) => Array.isArray(value) && value.includes(opt.value))
        : options.find((opt) => opt.value === value) || null;

    const handleChange = (opt: SingleValue<CreatableNameOption> | MultiValue<CreatableNameOption>) => {
        if (isMulti) {
            (onChange as (next: CreatableNameOption[]) => void)([...(opt as MultiValue<CreatableNameOption>)]);
            return;
        }
        (onChange as (next: CreatableNameOption | null) => void)((opt as SingleValue<CreatableNameOption>) ?? null);
    };

    return (
        <CreatableSelect<CreatableNameOption, boolean>
            inputId={inputId}
            options={options}
            isClearable={isClearable}
            isDisabled={disabled || creating}
            isLoading={creating}
            isMulti={isMulti}
            placeholder={placeholder}
            formatCreateLabel={formatCreateLabel}
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            menuPosition="fixed"
            styles={{
                ...selectTheme,
                menuPortal: (base) => ({
                    ...base,
                    zIndex: 2000,
                }),
            }}
            className={className}
            components={disabled ? undefined : selectComponents}
            isValidNewOption={allowCreate ? undefined : () => false}
            value={selectedValue}
            onChange={handleChange}
            onCreateOption={
                allowCreate
                    ? (input) => {
                          void handleCreate(input);
                      }
                    : undefined
            }
        />
    );
};

export default CreatableNameSelect;
